import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import coursesService from '../services/CoursesService';
import { useUsuarioWithRole } from './useUsuarioWithRole';

const emptyStatus = {
  hasWelcomeCourse: false,
  courseId: null,
  courseTitle: null,
  isEnrolled: false,
  isCompleted: false,
  canAccessOtherCourses: true,
};

/**
 * Estado del curso de bienvenida obligatorio para el usuario autenticado.
 * Admin/profesor no quedan bloqueados en la UI de estudiante.
 */
export const useWelcomeCourseStatus = () => {
  const navigate = useNavigate();
  const { usuarioConCedula, isAdminOrTeacher, isLoading } = useUsuarioWithRole();
  const userId = usuarioConCedula?.id ?? usuarioConCedula?.Id ?? null;

  const [status, setStatus] = useState(emptyStatus);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    if (!userId || isAdminOrTeacher) {
      setStatus(emptyStatus);
      setLoading(false);
      setError(null);
      return emptyStatus;
    }

    setLoading(true);
    setError(null);
    try {
      const data = await coursesService.getWelcomeStatus(userId, navigate);
      setStatus(data);
      return data;
    } catch (err) {
      console.error('Error al obtener welcome-status:', err);
      setError(err);
      setStatus(emptyStatus);
      return emptyStatus;
    } finally {
      setLoading(false);
    }
  }, [userId, isAdminOrTeacher, navigate]);

  useEffect(() => {
    if (isLoading) return;
    refresh();
  }, [isLoading, refresh]);

  const isBlocked =
    Boolean(userId) &&
    !isAdminOrTeacher &&
    status.hasWelcomeCourse &&
    !status.canAccessOtherCourses;

  const isWelcomeCourse = useCallback(
    (courseId) => {
      if (courseId == null || status.courseId == null) return false;
      return String(courseId) === String(status.courseId);
    },
    [status.courseId]
  );

  return {
    ...status,
    loading: isLoading || loading,
    error,
    isBlocked,
    isWelcomeCourse,
    refresh,
    userId,
  };
};

export default useWelcomeCourseStatus;
